/**
 * Merchant Product Modal Redux Slice
 * 
 * Manages state for merchant product modal including modal visibility,
 * operation mode, selected products, generated SKUs, and search results.
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { MerchantProduct } from 'src/shared/components/MerchantProductModal/types'
import { SKUGenerationResult, skuGenerationService } from '../services/skuGenerationService'

export interface MerchantProductModalState {
  // Modal state
  isOpen: boolean
  mode: 'link' | 'copy'
  currentView: 'welcome' | 'productList'
  isFirstTimeUser: boolean
  linkTarget: { type: 'variant'; index: number } | null
  
  // Product selection
  selectedProducts: MerchantProduct[]
  selectedProductId: string | null
  
  // Search and filtering
  searchQuery: string
  searchResults: MerchantProduct[]
  isLoading: boolean
  error: string | null
  
  // Pagination
  currentPage: number
  totalPages: number
  totalItems: number
  itemsPerPage: number
  
  // Filters
  filters: {
    category: string[]
    brand: string[]
    supplier: string[]
    status: ('active' | 'inactive')[]
    marketplace: string[]
  }
  
  // SKU generation
  generatedSKUs: SKUGenerationResult[]
  skuGenerationInProgress: boolean
  skuGenerationError: string | null
  
  // Bulk operations
  bulkSelection: string[]
  bulkOperationInProgress: boolean
}

const initialState: MerchantProductModalState = {
  // Modal state
  isOpen: false,
  mode: 'link',
  currentView: 'welcome',
  isFirstTimeUser: false,
  linkTarget: null,
  
  // Product selection
  selectedProducts: [],
  selectedProductId: null,
  
  // Search and filtering
  searchQuery: '',
  searchResults: [],
  isLoading: false,
  error: null,
  
  // Pagination
  currentPage: 1,
  totalPages: 0,
  totalItems: 0,
  itemsPerPage: 12,
  
  // Filters
  filters: {
    category: [],
    brand: [],
    supplier: [],
    status: [],
    marketplace: []
  },
  
  // SKU generation
  generatedSKUs: [],
  skuGenerationInProgress: false,
  skuGenerationError: null,
  
  // Bulk operations
  bulkSelection: [],
  bulkOperationInProgress: false
}

// Async thunks
export const searchMerchantProducts = createAsyncThunk(
  'merchantProductModal/searchProducts',
  async (params: {
    query: string
    page: number
    filters: MerchantProductModalState['filters']
  }, { rejectWithValue }) => {
    try {
      // In real implementation, this would call API
      // For now, using mock data
      const mockResults: MerchantProduct[] = [
        {
          id: '1',
          name: 'Premium Wireless Headphones',
          sku: 'AWH-001',
          brandName: 'AudioTech',
          description: 'High-quality wireless headphones with noise cancellation',
          isActive: true,
          category: [
            { id: 'electronics', level: 1, hasChildren: true }
          ],
          photos: [
            {
              url: 'https://via.placeholder.com/150/4F46E5/FFFFFF?text=Audio',
              hash: 'audio-image-hash',
              isPrimary: true,
              isActive: true
            }
          ]
        }
      ]
      
      return {
        products: mockResults,
        totalItems: mockResults.length,
        totalPages: Math.ceil(mockResults.length / params.page)
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Search failed')
    }
  }
)

export const generateProductSKUs = createAsyncThunk(
  'merchantProductModal/generateSKUs',
  async (params: {
    products: MerchantProduct[]
    pattern: string
    options: Record<string, any>
  }, { rejectWithValue }) => {
    try {
      const results = params.products.map(product => {
        return skuGenerationService.generateSKU({
          pattern: params.pattern,
          brand: product.brand,
          category: product.category?.[0]?.id || '',
          supplier: product.supplier,
          product: product.name,
          ...params.options
        })
      })
      
      return results
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'SKU generation failed')
    }
  }
)

export const linkMerchantProducts = createAsyncThunk(
  'merchantProductModal/linkProducts',
  async (params: {
    productIds: string[]
    targetType: 'master' | 'variant'
    targetIndex?: number
  }, { rejectWithValue }) => {
    try {
      // In real implementation, this would call API to create links
      await new Promise(resolve => setTimeout(resolve, 1000)) // Simulate API call
      return { success: true, linkedCount: params.productIds.length }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Linking failed')
    }
  }
)

export const copyMerchantProducts = createAsyncThunk(
  'merchantProductModal/copyProducts',
  async (params: {
    productIds: string[]
    modifications: Record<string, any>
  }, { rejectWithValue }) => {
    try {
      // In real implementation, this would call API to create copies
      await new Promise(resolve => setTimeout(resolve, 1500)) // Simulate API call
      return { success: true, copiedCount: params.productIds.length }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Copying failed')
    }
  }
)

// Main slice
const merchantProductModalSlice = createSlice({
  name: 'merchantProductModal',
  initialState,
  reducers: {
    // Modal management
    openModal: (state, action: PayloadAction<{
      mode: 'link' | 'copy'
      isFirstTimeUser?: boolean
      linkTarget?: { type: 'variant'; index: number } | null
    }>) => {
      state.isOpen = true
      state.mode = action.payload.mode
      state.isFirstTimeUser = action.payload.isFirstTimeUser || false
      state.linkTarget = action.payload.linkTarget || null
      state.currentView = 'welcome'
      state.error = null
    },

    closeModal: (state) => {
      state.isOpen = false
      state.currentView = 'welcome'
      state.selectedProducts = []
      state.selectedProductId = null
      state.searchQuery = ''
      state.searchResults = []
      state.bulkSelection = []
      state.generatedSKUs = []
      state.error = null
    },

    setCurrentView: (state, action: PayloadAction<'welcome' | 'productList'>) => {
      state.currentView = action.payload
    },

    // Product selection
    selectProduct: (state, action: PayloadAction<MerchantProduct>) => {
      state.selectedProductId = action.payload.id
      const exists = state.selectedProducts.find(p => p.id === action.payload.id)
      if (!exists) {
        state.selectedProducts.push(action.payload)
      }
    },

    unselectProduct: (state, action: PayloadAction<string>) => {
      state.selectedProductId = null
      state.selectedProducts = state.selectedProducts.filter(p => p.id !== action.payload)
    },

    clearSelection: (state) => {
      state.selectedProducts = []
      state.selectedProductId = null
      state.bulkSelection = []
    },

    // Bulk selection
    toggleBulkSelection: (state, action: PayloadAction<string>) => {
      const productId = action.payload
      const index = state.bulkSelection.indexOf(productId)
      if (index === -1) {
        state.bulkSelection.push(productId)
      } else {
        state.bulkSelection.splice(index, 1)
      }
    },

    selectAllProducts: (state) => {
      state.bulkSelection = state.searchResults.map(p => p.id)
    },

    clearBulkSelection: (state) => {
      state.bulkSelection = []
    },

    // Search and filtering
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload
      state.currentPage = 1 // Reset to first page on new search
    },

    setFilters: (state, action: PayloadAction<Partial<MerchantProductModalState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload }
      state.currentPage = 1 // Reset to first page on filter change
    },

    clearFilters: (state) => {
      state.filters = {
        category: [],
        brand: [],
        supplier: [],
        status: [],
        marketplace: []
      }
      state.currentPage = 1
    },

    // Pagination
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload
    },

    // SKU generation
    clearGeneratedSKUs: (state) => {
      state.generatedSKUs = []
      state.skuGenerationError = null
    },

    // Error handling
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
    },

    clearError: (state) => {
      state.error = null
    }
  },

  extraReducers: (builder) => {
    // Search products
    builder
      .addCase(searchMerchantProducts.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(searchMerchantProducts.fulfilled, (state, action) => {
        state.isLoading = false
        state.searchResults = action.payload.products
        state.totalItems = action.payload.totalItems
        state.totalPages = action.payload.totalPages
      })
      .addCase(searchMerchantProducts.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // SKU generation
    builder
      .addCase(generateProductSKUs.pending, (state) => {
        state.skuGenerationInProgress = true
        state.skuGenerationError = null
      })
      .addCase(generateProductSKUs.fulfilled, (state, action) => {
        state.skuGenerationInProgress = false
        state.generatedSKUs = action.payload
      })
      .addCase(generateProductSKUs.rejected, (state, action) => {
        state.skuGenerationInProgress = false
        state.skuGenerationError = action.payload as string
      })

    // Link products
    builder
      .addCase(linkMerchantProducts.pending, (state) => {
        state.bulkOperationInProgress = true
        state.error = null
      })
      .addCase(linkMerchantProducts.fulfilled, (state) => {
        state.bulkOperationInProgress = false
        // Modal will be closed by parent component
      })
      .addCase(linkMerchantProducts.rejected, (state, action) => {
        state.bulkOperationInProgress = false
        state.error = action.payload as string
      })

    // Copy products
    builder
      .addCase(copyMerchantProducts.pending, (state) => {
        state.bulkOperationInProgress = true
        state.error = null
      })
      .addCase(copyMerchantProducts.fulfilled, (state) => {
        state.bulkOperationInProgress = false
        // Modal will be closed by parent component
      })
      .addCase(copyMerchantProducts.rejected, (state, action) => {
        state.bulkOperationInProgress = false
        state.error = action.payload as string
      })
  }
})

// Export actions
export const {
  openModal,
  closeModal,
  setCurrentView,
  selectProduct,
  unselectProduct,
  clearSelection,
  toggleBulkSelection,
  selectAllProducts,
  clearBulkSelection,
  setSearchQuery,
  setFilters,
  clearFilters,
  setCurrentPage,
  clearGeneratedSKUs,
  setError,
  clearError
} = merchantProductModalSlice.actions

// Export selectors
export const selectMerchantProductModal = (state: any) => state.merchantProductModal || {}
export const selectIsModalOpen = (state: any) => state.merchantProductModal?.isOpen || false
export const selectCurrentView = (state: any) => state.merchantProductModal?.currentView || 'welcome'
export const selectSelectedProducts = (state: any) => state.merchantProductModal?.selectedProducts || []
export const selectSearchResults = (state: any) => state.merchantProductModal?.searchResults || []
export const selectIsLoading = (state: any) => state.merchantProductModal?.isLoading || false
export const selectBulkSelection = (state: any) => state.merchantProductModal?.bulkSelection || []
export const selectGeneratedSKUs = (state: any) => state.merchantProductModal?.generatedSKUs || []
export const selectModalError = (state: any) => state.merchantProductModal?.error || null

// Export reducer
export default merchantProductModalSlice.reducer